<template>
  <v-expansion-panel class="section-panel">
    <v-expansion-panel-header class="panel-header">
      <div class="d-flex align-center justify-space-between">
        <div class="d-flex align-center">
          <v-icon :color="iconColor" class="mr-3">{{ icon }}</v-icon>
          <div>
            <div class="section-name">{{ title }}</div>
            <div v-if="description" class="section-description">{{ description }}</div>
          </div>
        </div>
        <div class="format-preview" @click.stop>
          <v-chip small outlined color="primary" class="mr-2">
            Font {{ value.fontType }}
          </v-chip>
          <v-chip small outlined color="primary" class="mr-2">
            Veličina {{ value.fontSize }}
          </v-chip>
          <v-chip small outlined :color="value.bold ? 'success' : 'grey'">
            {{ value.bold ? 'Bold' : 'Normal' }}
          </v-chip>
        </div>
      </div>
    </v-expansion-panel-header>

    <v-expansion-panel-content class="panel-content">
      <v-card flat class="controls-card pa-4">
        <!-- Font Type Selector -->
        <div class="control-group mb-4">
          <div class="control-label mb-2">
            <v-icon small color="primary" class="mr-2">mdi-format-font</v-icon>
            Tip Fonta
          </div>
          <v-btn-toggle
            :value="value.fontType"
            @change="updateFontType"
            mandatory
            dense
            color="primary"
            class="font-toggle"
          >
            <v-btn value="A" small>
              <span class="font-btn-text">Font A</span>
            </v-btn>
            <v-btn value="B" small>
              <span class="font-btn-text">Font B</span>
            </v-btn>
            <v-btn value="C" small>
              <span class="font-btn-text">Font C</span>
            </v-btn>
          </v-btn-toggle>
          <div class="control-hint mt-1">
            Font A je najčitljiviji, Font C je najmanji
          </div>
        </div>

        <!-- Font Size Slider -->
        <div class="control-group mb-4">
          <div class="control-label mb-2">
            <v-icon small color="primary" class="mr-2">mdi-format-size</v-icon>
            Veličina Fonta
          </div>
          <v-slider
            :value="value.fontSize"
            @change="updateFontSize"
            :tick-labels="['Normal', 'Srednji', 'Velik', 'Veći', 'Ekstra']"
            :max="4"
            :min="0"
            step="1"
            ticks="always"
            tick-size="4"
            color="primary"
            track-color="grey lighten-2"
            thumb-label="always"
          >
            <template v-slot:thumb-label="{ value: thumbValue }">
              {{ thumbValue }}
            </template>
          </v-slider>
          <div class="control-hint">
            Približno {{ calculateCharsPerLine }} znakova po liniji na {{ printerWidth }}mm papiru
          </div>
        </div>

        <!-- Bold Toggle -->
        <div class="control-group mb-4">
          <div class="control-label mb-2">
            <v-icon small color="primary" class="mr-2">mdi-format-bold</v-icon>
            Podebljano
          </div>
          <v-switch
            :input-value="value.bold"
            @change="updateBold"
            color="primary"
            inset
            class="mt-0 pt-0"
            hide-details
          >
            <template v-slot:label>
              <span class="switch-label">
                {{ value.bold ? 'Podebljano (Bold)' : 'Normalno' }}
              </span>
            </template>
          </v-switch>
        </div>

        <!-- Alignment Buttons -->
        <div class="control-group mb-4">
          <div class="control-label mb-2">
            <v-icon small color="primary" class="mr-2">mdi-format-align-left</v-icon>
            Poravnanje
          </div>
          <v-btn-toggle
            :value="value.alignment"
            @change="updateAlignment"
            mandatory
            dense
            color="primary"
            class="alignment-toggle"
          >
            <v-btn :value="0" small>
              <v-icon small>mdi-format-align-left</v-icon>
              <span class="ml-2">Lijevo</span>
            </v-btn>
            <v-btn :value="1" small>
              <v-icon small>mdi-format-align-center</v-icon>
              <span class="ml-2">Centar</span>
            </v-btn>
            <v-btn :value="2" small>
              <v-icon small>mdi-format-align-right</v-icon>
              <span class="ml-2">Desno</span>
            </v-btn>
          </v-btn-toggle>
        </div>

        <!-- Line Spacing Toggle -->
        <div class="control-group">
          <div class="control-label mb-2">
            <v-icon small color="primary" class="mr-2">mdi-format-line-spacing</v-icon>
            Razmak Između Redova
          </div>
          <v-btn-toggle
            :value="value.lineSpacing"
            @change="updateLineSpacing"
            mandatory
            dense
            color="primary"
            class="spacing-toggle"
          >
            <v-btn :value="0" small>
              <v-icon small>mdi-arrow-collapse-vertical</v-icon>
              <span class="ml-2">Manji</span>
            </v-btn>
            <v-btn :value="1" small>
              <v-icon small>mdi-format-line-spacing</v-icon>
              <span class="ml-2">Normalno</span>
            </v-btn>
            <v-btn :value="2" small>
              <v-icon small>mdi-arrow-expand-vertical</v-icon>
              <span class="ml-2">Veći</span>
            </v-btn>
          </v-btn-toggle>
          <div class="control-hint mt-1">
            Manji = zbijeno, Normalno = standard, Veći = dodatan razmak
          </div>
        </div>
      </v-card>
    </v-expansion-panel-content>
  </v-expansion-panel>
</template>

<script>
export default {
  name: 'PrintSectionCustomizer',

  props: {
    title: {
      type: String,
      required: true
    },
    description: {
      type: String,
      default: ''
    },
    icon: {
      type: String,
      default: 'mdi-cog'
    },
    iconColor: {
      type: String,
      default: 'primary'
    },
    value: {
      type: Object,
      required: true,
      validator: (val) => {
        return (
          val.hasOwnProperty('fontType') &&
          val.hasOwnProperty('fontSize') &&
          val.hasOwnProperty('bold') &&
          val.hasOwnProperty('alignment') &&
          val.hasOwnProperty('lineSpacing')
        )
      }
    },
    printerWidth: {
      type: Number,
      default: 80 // 80mm default
    }
  },

  computed: {
    /**
     * Calculate approximate characters per line based on font settings
     * This is an estimation based on typical ESC/POS printer behavior
     */
    calculateCharsPerLine () {
      // Base characters per line for 80mm paper with font A, size 0
      const baseChars = this.printerWidth === 80 ? 48 : 32

      // Font type adjustment
      let fontMultiplier = 1.0
      if (this.value.fontType === 'B') fontMultiplier = 1.2
      else if (this.value.fontType === 'C') fontMultiplier = 1.4

      // Size adjustment with finer granularity
      // 0=Normal(1.0x), 1=Srednji(1.25x), 2=Velik(1.5x), 3=Veći(2.0x), 4=Ekstra(2.5x)
      const sizeMultipliers = [1.0, 1.25, 1.5, 2.0, 2.5]
      const scaleFactor = sizeMultipliers[this.value.fontSize] || 1.0
      const sizeMultiplier = 1.0 / scaleFactor

      const chars = Math.floor(baseChars * fontMultiplier * sizeMultiplier)
      return chars
    }
  },

  methods: {
    updateFontType (fontType) {
      this.$emit('input', { ...this.value, fontType })
    },

    updateFontSize (fontSize) {
      this.$emit('input', { ...this.value, fontSize })
    },

    updateBold (bold) {
      this.$emit('input', { ...this.value, bold })
    },

    updateAlignment (alignment) {
      this.$emit('input', { ...this.value, alignment })
    },

    updateLineSpacing (lineSpacing) {
      this.$emit('input', { ...this.value, lineSpacing })
    }
  }
}
</script>

<style scoped>
.section-panel {
  background: rgba(255, 255, 255, 0.98) !important;
  border: 1px solid rgba(0, 179, 190, 0.15) !important;
  border-radius: 8px !important;
  overflow: hidden;
  margin-bottom: 12px;
}

.panel-header {
  background: rgba(0, 179, 190, 0.03) !important;
  min-height: 64px !important;
}

.section-name {
  font-size: 16px !important;
  font-weight: 600 !important;
  color: #2c3e50 !important;
}

.section-description {
  font-size: 12px !important;
  color: #7f8c8d !important;
  margin-top: 2px;
}

.format-preview {
  display: flex;
  align-items: center;
}

.panel-content {
  background: white !important;
}

.controls-card {
  background: rgba(0, 179, 190, 0.02) !important;
  border-radius: 8px !important;
}

.control-group {
  border-bottom: 1px solid rgba(0, 179, 190, 0.1);
  padding-bottom: 16px;
}

.control-group:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.control-label {
  font-size: 14px !important;
  font-weight: 600 !important;
  color: #34495e !important;
  display: flex;
  align-items: center;
}

.control-hint {
  font-size: 12px !important;
  color: #7f8c8d !important;
  font-style: italic;
}

.font-toggle,
.alignment-toggle,
.spacing-toggle {
  width: 100%;
}

.font-toggle >>> .v-btn,
.alignment-toggle >>> .v-btn,
.spacing-toggle >>> .v-btn {
  flex: 1;
}

.font-btn-text {
  font-size: 13px !important;
  text-transform: none !important;
}

.switch-label {
  font-size: 14px !important;
  color: #2c3e50 !important;
}

/* Slider customization */
.v-slider >>> .v-slider__tick {
  background-color: rgba(0, 179, 190, 0.3) !important;
}

.v-slider >>> .v-slider__tick--filled {
  background-color: rgba(0, 179, 190, 0.8) !important;
}

.v-slider >>> .v-slider__tick-label {
  font-size: 11px !important;
  color: #7f8c8d !important;
}

@media (max-width: 600px) {
  .format-preview {
    flex-wrap: wrap;
    gap: 4px;
  }

  .section-name {
    font-size: 14px !important;
  }

  .control-label {
    font-size: 13px !important;
  }

  .font-btn-text {
    font-size: 11px !important;
  }
}
</style>
